import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DashboardLayout } from "./DashboardLayout";
import { api, MXN, fileUrl } from "../../lib/apiClient";
import { TAG_LABELS } from "../../lib/constants";
import { toast } from "sonner";
import { AlertTriangle, Check, Plus, Trash2, Save, X, Upload, Loader2, Pencil, Tag } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

const EMPTY = { name: "", description: "", category_id: "", base_price: 0, images: [], available: true, tags: [], sizes: [], portions: [], flavors: [], fillings: [], decorations: [], extras: [], min_advance_days: 2, requires_quote: false, product_type: "pastel" };
const TAGS = ["mas-vendido", "nuevo", "temporada", "bajo-pedido"];
const TYPES = [["pastel", "Pastel"], ["cupcake", "Cupcake"], ["cuchareable", "Cuchareable"], ["panaderia", "Panadería"], ["evento", "Evento"]];
const LIST_KEYS = ["sizes", "flavors", "fillings", "decorations", "extras"];

const withListDrafts = (p) => ({
  ...p,
  ...Object.fromEntries(LIST_KEYS.map((key) => [`_${key}Text`, (p[key] || []).join(", ")])),
});
const parseList = (txt = "") => txt.split(",").map((x) => x.trim()).filter(Boolean);

export default function Catalogo() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [edit, setEdit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [editingCat, setEditingCat] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => api.get("/products", { params: { all: true } }).then((r) => setProducts(r.data)).catch(() => {}), []);
  const loadCats = useCallback(() => api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}), []);
  const loadAll = useCallback(() => { load(); loadCats(); }, [load, loadCats]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useAdminRealtime(loadAll, ["catalog"]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const save = async () => {
    if (!edit.name) { toast.error("El producto necesita nombre."); return; }
    const body = { ...edit, base_price: parseFloat(edit.base_price) || 0, min_advance_days: parseInt(edit.min_advance_days) || 0 };
    LIST_KEYS.forEach((key) => {
      body[key] = parseList(edit[`_${key}Text`] ?? (edit[key] || []).join(", "));
      delete body[`_${key}Text`];
    });
    delete body.id;
    if (edit.id) await api.put(`/products/${edit.id}`, body);
    else await api.post("/products", body);
    toast.success("Producto guardado 🍰"); setEdit(null); load();
  };
  const requestDelProduct = (p) => setConfirm({
    title: "¿Eliminar producto?",
    text: `Se eliminará “${p.name}” del catálogo. Esta acción no se puede deshacer.`,
    action: async () => { await api.delete(`/products/${p.id}`); load(); toast.success("Producto eliminado"); },
  });
  const toggle = async (p) => { await api.put(`/products/${p.id}`, { ...p, id: undefined }); };

  const uploadImg = async (file) => {
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((e) => ({ ...e, images: [...(e.images || []), fileUrl(data.path)] }));
    } catch { toast.error("No se pudo subir la imagen."); } setUploading(false);
  };

  const addCat = async () => { if (!newCat) return; await api.post("/categories", { name: newCat, order: categories.length }); setNewCat(""); loadCats(); toast.success("Categoría agregada"); };
  const startEditCat = (c) => setEditingCat({ ...c });
  const saveCat = async () => {
    if (!editingCat?.name) { toast.error("La categoría necesita nombre."); return; }
    await api.put(`/categories/${editingCat.id}`, { name: editingCat.name, icon: editingCat.icon || "Cake", order: editingCat.order || 0 });
    setEditingCat(null); loadCats(); toast.success("Categoría actualizada");
  };
  const requestDelCat = (c) => setConfirm({
    title: "¿Eliminar categoría?",
    text: `Se eliminará “${c.name}”. Los productos que la usen quedarán sin categoría.`,
    action: async () => {
      try {
        await api.delete(`/categories/${c.id}`);
        loadCats();
        toast.success("Categoría eliminada");
      } catch (e) {
        toast.warning(e.response?.data?.detail || "No se puede eliminar esta categoría.");
        throw e;
      }
    },
  });

  const listField = (label, key) => (
    <div><label className="text-xs text-cafe/60">{label} (separa con comas)</label>
      <input data-testid={`prod-${key}`} value={edit[`_${key}Text`] ?? (edit[key] || []).join(", ")}
        onChange={(e) => setEdit({ ...edit, [`_${key}Text`]: e.target.value })}
        onBlur={() => setEdit((prev) => ({ ...prev, [key]: parseList(prev[`_${key}Text`] || "") }))}
        className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino" /></div>
  );

  return (
    <DashboardLayout title="Catálogo">
      <div className="grid grid-cols-1 min-[380px]:grid-cols-2 sm:flex sm:flex-wrap gap-3 mb-5">
        <button onClick={() => setEdit(withListDrafts({ ...EMPTY }))} data-testid="new-product" className="w-full sm:w-auto justify-center bg-vino text-crema rounded-full px-6 py-2.5 text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Nuevo producto</button>
        <button onClick={() => setShowCats(true)} data-testid="manage-cats" className="w-full sm:w-auto justify-center border-2 border-vino text-vino rounded-full px-6 py-2.5 text-sm flex items-center gap-2"><Tag className="w-4 h-4" /> Categorías</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`cat-product-${p.id}`} className="bg-blancoCalido border border-beige rounded-[22px] overflow-hidden shadow-pastel">
            <div className="h-32 bg-beige relative">{p.images?.[0] && <img src={p.images[0]} className="w-full h-full object-cover" alt="" />}
              {!p.available && <span className="absolute inset-0 bg-vinoDark/50 text-crema flex items-center justify-center text-sm">No disponible</span>}</div>
            <div className="p-4">
              <p className="font-serif text-lg text-vino leading-tight">{p.name}</p>
              <p className="text-xs text-cafe/50">{catName(p.category_id)} · {p.requires_quote ? "Cotización" : MXN(p.base_price)}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEdit(withListDrafts(p))} data-testid={`edit-${p.id}`} className="flex-1 bg-vino/10 text-vino rounded-full py-2 text-xs flex items-center justify-center gap-1"><Pencil className="w-3 h-3" /> Editar</button>
                <label className="flex items-center gap-1 text-xs text-cafe/60 cursor-pointer">
                  <input type="checkbox" checked={p.available} onChange={(e) => { const np = { ...p, available: e.target.checked }; setProducts((prev) => prev.map((x) => x.id === p.id ? np : x)); toggle(np); }} data-testid={`toggle-${p.id}`} /> Activo
                </label>
                <button onClick={() => requestDelProduct(p)} data-testid={`del-${p.id}`} className="text-vino p-2 hover:bg-beige rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Editor */}
      {edit && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="product-editor">
          <div className="absolute inset-0 bg-vinoDark/30 backdrop-blur-sm" onClick={() => setEdit(null)} />
          <div className="relative w-full max-w-lg bg-crema h-full overflow-y-auto overflow-x-hidden p-4 sm:p-6 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-6">
            <div className="flex justify-between items-center gap-3 mb-4"><h2 className="font-serif text-2xl text-vino truncate">{edit.id ? "Editar" : "Nuevo"} producto</h2><button onClick={() => setEdit(null)} className="text-vino p-2 shrink-0"><X /></button></div>
            <div className="space-y-3">
              <div><label className="text-xs text-cafe/60">Nombre</label><input data-testid="prod-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
              <div><label className="text-xs text-cafe/60">Descripción</label><textarea data-testid="prod-desc" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={2} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                <div><label className="text-xs text-cafe/60">Categoría</label>
                  <select data-testid="prod-category" value={edit.category_id} onChange={(e) => setEdit({ ...edit, category_id: e.target.value })} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino">
                    <option value="">Sin categoría</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select></div>
                <div><label className="text-xs text-cafe/60">Tipo</label>
                  <select data-testid="prod-type" value={edit.product_type} onChange={(e) => setEdit({ ...edit, product_type: e.target.value })} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino">
                    {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></div>
              </div>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                <div><label className="text-xs text-cafe/60">Precio base (MXN)</label><input data-testid="prod-price" type="number" value={edit.base_price} onChange={(e) => setEdit({ ...edit, base_price: e.target.value })} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
                <div><label className="text-xs text-cafe/60">Anticipación (días)</label><input type="number" value={edit.min_advance_days} onChange={(e) => setEdit({ ...edit, min_advance_days: e.target.value })} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
              </div>
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3 text-sm">
                <label className="flex items-center gap-2 text-cafe/70"><input type="checkbox" checked={edit.available} onChange={(e) => setEdit({ ...edit, available: e.target.checked })} data-testid="prod-available" /> Disponible</label>
                <label className="flex items-center gap-2 text-cafe/70"><input type="checkbox" checked={edit.requires_quote} onChange={(e) => setEdit({ ...edit, requires_quote: e.target.checked })} data-testid="prod-quote" /> Requiere cotización</label>
              </div>
              <div><label className="text-xs text-cafe/60">Etiquetas</label>
                <div className="flex flex-wrap gap-2 mt-1">{TAGS.map((t) => (
                  <button key={t} onClick={() => setEdit({ ...edit, tags: (edit.tags || []).includes(t) ? edit.tags.filter((x) => x !== t) : [...(edit.tags || []), t] })}
                    className={`text-xs px-3 py-1.5 rounded-full border-2 ${(edit.tags || []).includes(t) ? "bg-dorado text-crema border-dorado" : "border-beige text-cafe/60"}`}>{TAG_LABELS[t]}</button>
                ))}</div></div>
              {listField("Tamaños", "sizes")}
              {listField("Sabores", "flavors")}
              {listField("Rellenos", "fillings")}
              {listField("Decoraciones", "decorations")}
              {listField("Extras", "extras")}
              <div><label className="text-xs text-cafe/60">Imágenes</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(edit.images || []).map((img) => <div key={img} className="relative w-16 h-16"><img src={img} className="w-full h-full object-cover rounded-lg" alt="" /><button onClick={() => setEdit({ ...edit, images: edit.images.filter((x) => x !== img) })} className="absolute -top-1 -right-1 bg-vino text-crema rounded-full p-0.5"><X className="w-3 h-3" /></button></div>)}
                  <label data-testid="prod-upload" className="w-16 h-16 rounded-lg border-2 border-dashed border-rosa flex items-center justify-center cursor-pointer text-vino">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}<input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} /></label>
                </div></div>
              <button onClick={save} data-testid="prod-save" className="w-full bg-vino text-crema rounded-full py-3 font-medium flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Guardar producto</button>
            </div>
          </div>
        </div>
      )}

      {/* Categories modal */}
      {showCats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="cats-modal">
          <div className="absolute inset-0 bg-vinoDark/30 backdrop-blur-sm" onClick={() => setShowCats(false)} />
          <div className="relative bg-crema rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-4 sm:p-6 max-h-[80vh] overflow-y-auto overflow-x-hidden">
            <div className="flex justify-between items-center mb-4"><h2 className="font-serif text-2xl text-vino">Categorías</h2><button onClick={() => setShowCats(false)} className="text-vino p-2"><X /></button></div>
            <div className="grid grid-cols-[1fr_auto] gap-2 mb-4"><input data-testid="new-cat" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nueva categoría" className="min-w-0 bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /><button onClick={addCat} data-testid="add-cat" className="bg-vino text-crema rounded-xl px-4"><Plus className="w-4 h-4" /></button></div>
            <div className="space-y-2">{categories.map((c) => (
              <div key={c.id} className="bg-blancoCalido border border-beige rounded-xl p-3">
                {editingCat?.id === c.id ? (
                  <div className="grid grid-cols-[1fr_auto_auto] gap-2">
                    <input value={editingCat.name || ""} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                      className="min-w-0 bg-crema border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino" />
                    <button onClick={saveCat} className="text-salvia p-2"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingCat(null)} className="text-vino p-2"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-sm text-cafe min-w-0 truncate">{c.name}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEditCat(c)} data-testid={`edit-cat-${c.id}`} className="text-vino p-1.5"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => requestDelCat(c)} data-testid={`del-cat-${c.id}`} className="text-vino p-1.5"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}</div>
          </div>
        </div>
      )}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </DashboardLayout>
  );
}

const ConfirmDialog = ({ confirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  if (!confirm) return null;
  const run = async () => {
    setBusy(true);
    try {
      await confirm.action();
      onClose();
    } catch {
      // El toast específico ya se mostró; mantenemos el modal para que pueda cancelar.
    } finally {
      setBusy(false);
    }
  };
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[70] flex items-center justify-center px-5">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-vinoDark/35 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
        <motion.div initial={{ opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.98 }}
          className="relative w-full max-w-sm bg-blancoCalido border border-beige rounded-[30px] p-6 shadow-[0_24px_70px_rgba(75,16,37,0.25)] text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-rosa/30 text-vino flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h3 className="font-serif text-2xl text-vino">{confirm.title}</h3>
          <p className="text-sm text-cafe/60 mt-2">{confirm.text}</p>
          <div className="grid grid-cols-2 gap-3 mt-6">
            <button disabled={busy} onClick={onClose} className="rounded-full border-2 border-beige text-cafe/70 py-3 text-sm">Cancelar</button>
            <button disabled={busy} onClick={run} className="rounded-full bg-vino text-crema py-3 text-sm flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Eliminar
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
